import Store from 'repatch';

import State from '../core/State';
import GoogleAdAccount from '../entity/GoogleAdAccount';
import { OrganizationDetailFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import GoogleOauthService from '../service/GoogleOauthService';
import MessageDispatcher from '../service/MessageDispatcher';
import GoogleAdWebApi from '../service/persistent/GoogleAdWebApi';
import {
  decrement as decrementFullScreenSpinnerNecessity,
  increment as incrementFullScreenSpinnerNecessity,
  setLoadingMessage,
} from '../thunk/fullScreenSpinner';
import {
  disposeGoogleAdAccounts,
  finishIntegratedGoogleCommunication,
  setGoogleAdAccounts,
  setIntegratedGoogleAdAccounts,
  startIntegratedGoogleAdAccounts,
} from '../thunk/googleAdAccount';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';

class IntegrateGoogleAdAccountUsecase {
  private store: Store<State>;
  private errorPresenter: ErrorPresenter;
  private googleOauthService: GoogleOauthService;
  private messageDispatcher: MessageDispatcher;
  private googleAdWebApi: GoogleAdWebApi;

  constructor({
    store,
    errorPresenter,
    googleOauthService,
    messageDispatcher,
    googleAdWebApi,
  }: {
    store: Store<State>;
    errorPresenter: ErrorPresenter;
    googleOauthService: GoogleOauthService;
    messageDispatcher: MessageDispatcher;
    googleAdWebApi: GoogleAdWebApi;
  }) {
    this.store = store;
    this.errorPresenter = errorPresenter;
    this.googleOauthService = googleOauthService;
    this.messageDispatcher = messageDispatcher;
    this.googleAdWebApi = googleAdWebApi;
  }

  public fetchIntegratedGoogleAdAccounts = async (orgId?: string): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedGoogleAdAccounts());

    try {
      const integratedGoogleAdAccounts = await this.googleAdWebApi.getAllIntegratedAdAccounts(
        orgId || this.getCurrentOrganization().id,
      );
      this.store.dispatch(setIntegratedGoogleAdAccounts(integratedGoogleAdAccounts));
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public registerGoogleTokenWithAdwordsScope = async (): Promise<boolean | void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    try {
      const authCode = await this.googleOauthService.authenticate();
      const googleAdAccounts = await this.googleAdWebApi.registerGoogleTokenWithAdwordsScope(
        authCode,
        this.getCurrentOrganization().id,
      );
      this.store.dispatch(setGoogleAdAccounts(googleAdAccounts));
      if (googleAdAccounts.size === 0) {
        this.messageDispatcher.notifyUnimportant('message.google_no_linkable_ad_account');
      }
      return true;
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
  };

  public integrateGoogleAdAccount = async (googleAdAccount: GoogleAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());

    try {
      this.store.dispatch(setLoadingMessage('message.google_now_integrating'));
      await this.googleAdWebApi.integrateAdAccount(googleAdAccount, this.getCurrentOrganization().id);
      this.messageDispatcher.notifyUnimportant('message.google_integrated');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedGoogleAdAccounts();
  };

  public revokeGoogleAdAccount = async (googleAdAccount: GoogleAdAccount): Promise<void> => {
    this.store.dispatch(incrementFullScreenSpinnerNecessity());
    this.store.dispatch(startIntegratedGoogleAdAccounts());
    try {
      await this.googleAdWebApi.revokeAdAccount(googleAdAccount.id, this.getCurrentOrganization().id);
      this.store.dispatch(finishIntegratedGoogleCommunication());
      this.messageDispatcher.notifyUnimportant('message.google_revoked');
    } catch (err) {
      this.errorPresenter.showAsNonFatal(err);
    } finally {
      this.store.dispatch(decrementFullScreenSpinnerNecessity());
    }
    this.fetchIntegratedGoogleAdAccounts();
  };

  public disposeAdAccounts = (): void => {
    this.store.dispatch(disposeGoogleAdAccounts());
  };

  public disposeAccessToken = (): void => {
    this.store.dispatch(disposeGoogleAdAccounts());
  };

  private getCurrentOrganization(): OrganizationDetailFragment {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    if (!organization) {
      throw Error('organization was not found');
    }
    this.store.dispatch(setLastAccessedOrganization(organization));
    return organization;
  }
}

export default IntegrateGoogleAdAccountUsecase;
